/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */
import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as moment from 'moment/moment';
import {Observable, of} from 'rxjs';
import {map, pluck} from 'rxjs/operators';

import {DATE_FORMAT, ObserveType} from '../../app.constants';
import {ReportDefinition} from '../../models/models.interfaces';
import {ApiListData, ApiService} from '../../shared/api';
import {FileService} from '../../shared/files/file.service';
import {FileDescriptionsWithTotal} from '../../shared/files/files.model';
import {ReportFile} from '../../shared/legal-reports/legal-reports.interfaces';
import {LocalizationService} from '../../shared/localization/localization.service';
import {Headers} from '../../shared/utils/headers';
import {Payrolls} from '../../shared/utils/payrolls';

export interface PayEventsResponse {
  total: number;
  payload: ApiListData<any>;
}

export interface EventAction {
  payEventResultID: number;
  disableType?: string;
  actionType: string;
}

/**
 * Ongoing payrolls service that helps fetching payrolls and payrolls statistics, and execute pay actions
 */
@Injectable()
export class PayEventsService {

  constructor(private api: ApiService,
              private translate: TranslateService,
              private fileService: FileService,
              private localization: LocalizationService) {
  }

  public fetchPayEvents(search): Observable<PayEventsResponse> {
    search.observe = ObserveType.RESPONSE;
    return this.api
      .get('/payroll/api/v1/payEventResult', {requestOptions: search})
      .pipe(
        map(response => {
          return {
            payload: response.body,
            total: parseInt(Headers.get(response.headers, 'X-Total-Count'), 10)
          };
        })
      );
  }

  public fetchPayrollStatus(id: number): Observable<any> {
    return this.api.get(`/payroll/api/v1/payPeriod/${id}/reporting/payrollStatus`)
      .pipe(
        map(response => {
          const payload = <ApiListData<any>>response;
          return {
            payrollStatus: payload.data
          };
        })
      );
  }

  public setDonePayEvents(events: Array<EventAction>) {
    return this.api.post('/payroll/api/v1/payEventResult/setDone', events);
  }

  public unfreezePayrolls(id: number, payType: string) {
    return this.api.post(`/payroll/api/v1/payPeriod/${id}/unfreeze?pay-type=${payType}`);
  }

  public getEventStatus(id?: number): Observable<any> {
    return this.api.get(`/payroll/api/v1${id ? '/payPeriod/' + id : ''}/reporting/eventStatus`)
      .pipe(
        map(json => {
          return {
            statuses: json,
            statistics: {
              toValidate: json.filter(status => status.status === 'TODO')
                .reduce((count, status) => count + status.number, 0),
              validated: json.filter(status => status.status !== 'TODO')
                .reduce((count, status) => count + status.number, 0),
              countsByType: {
                error: json.filter(status => status.status === 'TODO' && status.eventType === 'ERROR_TECHNIC')
                  .reduce((count, status) => count + status.number, 0),
                information: json.filter(status => status.status === 'TODO' && status.eventType === 'INFORMATION')
                  .reduce((count, status) => count + status.number, 0),
                warning: json.filter(status => status.status === 'TODO' && status.eventType === 'WARNING')
                  .reduce((count, status) => count + status.number, 0),
                alert: json.filter(status => status.status === 'TODO' && status.eventType === 'ALERT')
                  .reduce((count, status) => count + status.number, 0)
              }
            }
          };
        })
      );
  }

  public getEventStatusChartAndStatistics(id?: number): Observable<any> {
    return this.getEventStatus(id)
      .pipe(
        map(result => {
          return {
            chart: this.createEventStatusChart(result),
            statistics: result.statistics
          };
        })
      );
  }

  public getEventStatusChartData(): Observable<any> {
    return this.getEventStatus().pipe(map(this.createEventStatusChart.bind(this)));
  }

  public getEventHistoric(): Observable<any> {
    return this.api.get('/payroll/api/v1/reporting/eventHistoric')
      .pipe(map(json => json.data));
  }

  public getCliRegEmpModel(id: number): Observable<any> {
    return this.api.get(`/repository/api/v1/cliReg/${id}/empModel`);
  }

  public getCliRegList(id: number): Observable<any> {
    return this.api.get(`/repository/api/v1/cliReg/?orgId=${id}`);
  }

  public getEventHistoricChartData() {
    return this.getEventHistoric()
      .pipe(
        map(result => {
          const historic = result;
          historic.reverse();

          const backlog = historic.map(entry => entry.attributes.backlog),
            closed = historic.map(entry => entry.attributes.nbClosed + entry.attributes.nbClosedByException),
            created = historic.map(entry => entry.attributes.nbCreated),
            legend = historic.map(entry => {
              return moment(entry.attributes.currentDate, DATE_FORMAT).format('D/M');
            });

          return {
            tooltip: {trigger: 'axis'},
            toolbox: {show: false},
            calculable: true,
            legend: {
              data: [
                this.translate.instant('CHARTS.PAYROLL.closed'),
                this.translate.instant('CHARTS.PAYROLL.created'),
                this.translate.instant('CHARTS.PAYROLL.backlog')
              ]
            },
            color: ['#6887ff', '#6cc788', '#2e3e4e'],
            xAxis: [{
              type: 'category',
              data: legend,
              axisLabel: {
                interval: 0,
                rotate: 45
              }
            }],
            yAxis: [{
              type: 'value'
            }],
            series: [
              {name: this.translate.instant('CHARTS.PAYROLL.created'), type: 'bar', data: created},
              {name: this.translate.instant('CHARTS.PAYROLL.closed'), type: 'bar', data: closed},
              {name: this.translate.instant('CHARTS.PAYROLL.backlog'), type: 'line', data: backlog}
            ]
          };
        })
      );
  }

  public getEventStatusByFamily(): Observable<any> {
    return this.api.get('/payroll/api/v1/reporting/eventStatusByFamily');
  }

  public getEventsPerDomainChart() {
    return this.getEventStatusByFamily()
      .pipe(
        map((chartData: any[]) => {
          if (chartData.length === 0) {
            return;
          }

          return {
            tooltip: {trigger: 'axis'},
            color: ['#af94e0'],
            calculable: true,
            xAxis: [{type: 'value'}],
            yAxis: [{
              type: 'category',
              show: false,
              data: chartData.map(status => this.translate.instant(status.family)),
              axisLabel: {interval: 0}
            }],
            series: [{
              type: 'bar',
              name: 'Number',
              data: chartData.map(status => status.number),
              itemStyle: {
                normal: {
                  label: {
                    show: true,
                    position: 'insideLeft',
                    formatter: '{b}',
                    textStyle: {color: '#000'}
                  }
                }
              }
            }]
          };
        })
      );
  }

  public getPayFiles(id: number, visibleType: string, perPage: number): Observable<FileDescriptionsWithTotal> {
    return this.fileService.fetchFiles(
      `search=payPeriodId==${id}&tags=visibleType==${visibleType},visibleType==BOTH&order-by=creationDate`, perPage);
  }

  public exportPayFilesToNifi(payFileIds: number[]): Observable<any> {
    return this.fileService.sendFiles(payFileIds);
  }

  public deletePayFiles(payFileIds: number[]): Observable<any> {
    return this.fileService.deleteFiles(payFileIds);
  }

  public downloadReport(reportId: number): Observable<Blob> {
    return this.fileService.getFile(reportId).pipe(pluck('blob'));
  }

  public closePeriod(id: number) {
    return this.api.post(`/payroll/api/v1/payPeriod/${id}/close`);
  }

  public createEventStatusChart(result: any) {
    const chartData = result.statuses.filter(status => status.status === 'TODO')
      .map(status => {
        return {
          value: status.number,
          name: Payrolls.getDisplayNameForEventType(status.eventType, this.translate),
          key: status.eventType,
          color: Payrolls.getColorForEventType(status.eventType)
        };
      });

    if (chartData.length === 0) {
      return;
    }

    const colors = chartData.map(data => data.color);

    return {
      tooltip: {trigger: 'item', formatter: '{a} <br/>{b} : {c} ({d}%)'},
      legend: {
        orient: 'horizontal',
        y: 'bottom',
        data: chartData.map(data => data.name)
      },
      calculable: true,
      color: colors,
      series: [{
        name: this.translate.instant('CHARTS.event_status'),
        type: 'pie',
        radius: ['50%', '70%'],
        center: ['50%', '43%'],
        itemStyle: {
          normal: {
            label: {
              formatter: '{c}',
              distance: 0.1
            }
          }
        },
        data: chartData
      }]
    };
  }

  public getComparisionData(sourcePeriod: number, targetPeriod: number, date: string, currency: string) {
    const parameters = `sourcePeriod=${sourcePeriod}&targetPeriod=${targetPeriod}&date=${date}&currency=${currency}`;
    return this.api.get(`/payroll/api/v1/payPeriod/comparision?${parameters}`);
  }

  public getPayrollOverviewData(periodId: number, date: string, currency: string) {
    return this.api.get(`/payroll/api/v1/payPeriod/${periodId}/overview?date=${date}&currency=${currency}`);
  }

  public getUploadableReports(cliRegIds: number[]): Observable<ReportDefinition[]> {
    if (!cliRegIds || cliRegIds.length <= 0) {
      return of([]);
    }
    const cliRegsParameters = cliRegIds.map((cliRegId: number) => `cli-reg-ids=${cliRegId}`).join('&');
    return this.api.get(`/repository/api/v1/regulation/reports?${cliRegsParameters}&only-uploadable=true`);
  }

  public uploadFile(file: File, data: ReportFile) {
    return this.fileService.uploadFile(file, new Blob([JSON.stringify(data)], {type: 'application/json'}));
  }

  public getWorkforceChartData(id?: number) {
    return this.api.get(`/payroll/api/v1/payPeriod/${id}/reporting/workforceAmount`)
      .pipe(
        map(result => {
          const data = result.data.sort(
              (item1, item2) => moment(item1.date).startOf('day').diff(moment(item2.date).startOf('day'), 'days')
            ),
            series = {};

          if (data.length === 0) {
            return null;
          }

          data.forEach(item => Object.keys(item.attributes).forEach(key => series[key] = []));
          data.forEach(item => Object.keys(series).forEach(serie => series[serie].push(item.attributes[serie] || 0)));

          return {
            grid: {
              x: 70,
              y2: 70
            },
            tooltip: {
              trigger: 'axis',
              formatter: (values) => {
                return this.getCharacterValues(values, 'DECIMAL_1_DIGIT');
              }
            },
            toolbox: {show: false},
            calculable: true,
            legend: {data: Object.keys(series).map(key => this.translate.instant(key))},
            color: ['#6887ff', '#6cc788'],
            xAxis: [{
              type: 'category',
              data: data.map(item => moment(item.date).format('ll')),
              axisLabel: {
                interval: 0,
                rotate: 45,
                margin: 5
              }
            }],
            yAxis: [{
              type: 'value'
            }],
            series: Object.keys(series)
              .map(key => ({
                name: this.translate.instant(key),
                type: 'bar',
                data: series[key]
              }))
          };
        })
      );
  }

  public getTotalPayrollsChartData(id?: number) {
    return this.api.get(`/payroll/api/v1/payPeriod/${id}/reporting/payrollAmount`)
      .pipe(
        map(result => {
          const data = result.data.sort(
              (item1, item2) => moment(item1.date).startOf('day').diff(moment(item2.date).startOf('day'), 'days')
            ),
            series = {};

          if (data.length === 0) {
            return null;
          }

          data.forEach(item => Object.keys(item.attributes).forEach(key => series[key] = []));
          data.forEach(item => Object.keys(series).forEach(serie => series[serie].push(item.attributes[serie] || 0)));

          return {
            grid: {
              x: 70,
              y2: 70
            },
            tooltip: {
              trigger: 'axis',
              formatter: (values) => {
                return this.getCharacterValues(values, 'DECIMAL_2_DIGIT');
              }
            },
            legend: {
              data: Object.keys(series).map(key => this.translate.instant(key))
            },
            calculable: true,
            color: ['#5FB3EF', '#B19ED8', '#2EC7C9'],
            xAxis: [
              {
                type: 'category',
                boundaryGap: false,
                data: data.map(item => moment(item.date).format('ll')),
                axisLabel: {
                  interval: 0,
                  rotate: 45
                }
              }
            ],
            yAxis: [{type: 'value'}],
            series: Object.keys(series).map(key => ({
              name: this.translate.instant(key),
              type: 'line',
              smooth: true,
              areaStyle: {normal: {}},
              data: series[key]
            }))
          };
        })
      );
  }

  private getCharacterValues(values: any, format: string) {
    let result = '';
    result += `${values[0].axisValue}<br/>`;
    values.forEach((element) => {
      result += `${
        element.marker
      } ${
        element.seriesName
      }: ${
        this.localization.localizeNumber(element.value, format)
      }<br/>`;
    });
    return result;
  }
}
